/**
 * Discover-only loading state. Shown while the recommendation feed is being
 * fetched. Mirrors the real 2-column quote grid with shimmering skeleton cards
 * plus a "curating" header so the wait reads as the engine actively matching
 * quotes to the user's taste. All motion is disabled under
 * `prefers-reduced-motion` (see the `.qa-*` rules in index.css).
 */

const SKELETON_LINES: string[][] = [
  ["w-full", "w-11/12", "w-3/4"],
  ["w-full", "w-5/6"],
  ["w-full", "w-10/12", "w-2/3", "w-1/2"],
  ["w-11/12", "w-4/5"],
];

function SkeletonCard({ lines }: { lines: string[] }) {
  return (
    <div className="qa-shimmer overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
      <div className="px-5 pt-5">
        {/* Header: badge + match chip */}
        <div className="mb-5 flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-stone-100" />
          <div className="h-5 w-20 rounded-full bg-stone-100" />
        </div>

        {/* Quote text */}
        <div className="space-y-2.5">
          {lines.map((w, i) => (
            <div key={i} className={`h-4 rounded-full bg-stone-100 ${w}`} />
          ))}
        </div>

        {/* Author + tags */}
        <div className="mt-6 flex items-center justify-between gap-3 border-t border-stone-100 pt-4">
          <div className="space-y-2">
            <div className="h-3.5 w-28 rounded-full bg-stone-100" />
            <div className="h-3 w-20 rounded-full bg-stone-100" />
          </div>
          <div className="flex gap-2">
            <div className="h-6 w-14 rounded-full bg-stone-100" />
            <div className="h-6 w-16 rounded-full bg-stone-100" />
          </div>
        </div>
      </div>

      {/* Footer action bar */}
      <div className="mt-4 flex items-center justify-between border-t border-stone-100 bg-stone-50 px-4 py-3">
        <div className="h-6 w-20 rounded-full bg-stone-200/70" />
        <div className="flex gap-2">
          <div className="h-6 w-16 rounded-full bg-stone-200/70" />
          <div className="h-6 w-16 rounded-full bg-stone-200/70" />
        </div>
      </div>
    </div>
  );
}

export function DiscoverLoadingState() {
  return (
    <div data-testid="discover-loading">
      {/* Curating header */}
      <div className="mb-6 flex flex-col items-center gap-3 rounded-3xl border border-stone-200 bg-white px-6 py-8 text-center shadow-sm">
        <div className="relative" aria-hidden="true">
          <div className="qa-float flex h-14 w-14 items-center justify-center rounded-2xl bg-stone-900 text-2xl text-white">
            ↗
          </div>
          <span className="qa-twinkle absolute -right-1.5 -top-1.5 text-lg text-stone-400">
            ✦
          </span>
        </div>
        <div>
          <p className="font-serif text-xl text-stone-900">Curating your quotes…</p>
          <p className="mt-1 text-sm leading-relaxed text-stone-500">
            Matching fresh voices to the taste you&rsquo;ve shown.
          </p>
        </div>
        <div className="qa-dots mt-1 flex gap-1.5" aria-hidden="true">
          <span className="h-1.5 w-1.5 rounded-full bg-stone-300" />
          <span className="h-1.5 w-1.5 rounded-full bg-stone-300" />
          <span className="h-1.5 w-1.5 rounded-full bg-stone-300" />
        </div>
      </div>

      {/* Skeleton grid mirrors the real 2-column feed */}
      <div className="grid gap-5 md:grid-cols-2" aria-hidden="true">
        {SKELETON_LINES.map((lines, i) => (
          <SkeletonCard key={i} lines={lines} />
        ))}
      </div>

      <span className="sr-only" role="status">
        Curating your quotes…
      </span>
    </div>
  );
}
